<template>
  <v-dialog v-model="dialog"
            persistent
            max-width="700px">
    <v-form @submit.prevent="submit"
            :disabled="form.busy">
      <v-card>
        <v-card-title class="pb-0">
          Quick Share: {{ presentation.name }}
        </v-card-title>
        <v-card-text>
          <v-list v-if="form.recipients.length"
                  flat
                  dense
                  outlined
                  class="py-0 mb-4">
            <template v-for="(recipient, index) in form.recipients">
              <v-list-item :key="recipient.id"
                           v-if="recipient && recipient.name"
                           dense>
                <v-list-item-title>
                  {{ recipient.name_email_company_record_type }}
                </v-list-item-title>
                <v-list-item-action>
                  <v-btn @click.prevent="removeRecipient(recipient)"
                         icon
                         title="Remove this recipient">
                    <v-icon>
                      fal fa-fw fa-times
                    </v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
              <v-divider v-if="index < (form.recipients.length - 1)"></v-divider>
            </template>
          </v-list>
          <v-alert v-if="canEmailSelf && recipientsContainsMyself"
                   type="info"
                   tile
                   dense>
            Avoid sending emails to yourself because they could go to your junk folder.
          </v-alert>

          <v-autocomplete v-model="form.contact"
                          ref="recipientAutocomplete"
                          :disabled="form.busy"
                          label="Who do you want to share this with?"
                          placeholder="Search for the recipient in your contacts."
                          prepend-inner-icon="fal fa-search"
                          :search-input.sync="search"
                          :items="normalizedContacts"
                          item-value="id"
                          item-text="name_email_company_record_type"
                          return-object
                          no-data-text="Search for the recipient in your contacts."
                          :error-messages="form.errors.get('contact')"
                          @change="checkContact">
            <template v-slot:append-item
                      v-if="isAllergan && normalizedContacts.length > 0">
              <v-list-item>
                <div class="mr-4">
                  <strong>"{{ search }}" not found in {{ crmLabel }}.</strong>
                </div>
                <div>
                  If you just added or changed an email address in {{ crmLabel }}, <a href="#" @click.prevent="syncFromMoxie">import from {{ crmLabel }}</a>.
                </div>
              </v-list-item>
            </template>
            <template v-else-if="search && search.trim() && normalizedContacts.length === 0"
                      v-slot:no-data>
              <v-list-item v-if="isAllergan">
                <div class="mr-4">
                  <strong>"{{ search }}" not found in {{ crmLabel }}.</strong>
                </div>
                <div>
                  If you just added or changed an email address in {{ crmLabel }}, <a href="#" @click.prevent="syncFromMoxie">import from {{ crmLabel }}</a>.
                </div>
              </v-list-item>
              <v-list-item v-else>
                <strong>"{{ search }}" not found.</strong>
              </v-list-item>
            </template>
          </v-autocomplete>

          <v-autocomplete v-if="canSelectTemplate"
                          v-model="form.share_template_id"
                          label="Which quick share email template do you want to use?"
                          :items="templates"
                          item-value="id"
                          item-text="name"
                          @change="selectTemplate">
            <template v-slot:item="{item: template}">
              <div class="py-1">
                <div class="text-subtitle-1">
                  {{ template.name }}
                </div>
                <div class="text-caption">
                  {{ template.subject }}
                </div>
              </div>
            </template>
          </v-autocomplete>

          <v-text-field v-else-if="requiredQuickShareTemplate"
                        label="The file will be shared using this email template"
                        :value="requiredQuickShareTemplate.name"
                        disabled
                        readonly
                        hide-details>
          </v-text-field>

          <template v-if="canShareLinks">
            <v-autocomplete v-model="form.presentation_id"
                            multiple
                            :items="shareablePresentations"
                            item-value="id"
                            item-text="name"
                            label="You can share other files"
                            deletable-chips
                            small-chips>
            </v-autocomplete>
          </template>

          <template v-if="canWriteCustomMessage">
            <v-textarea v-model="form.custom_message"
                        clearable
                        autocapitalize="off"
                        spellcheck="false"
                        :label="isCustomMessageRequired ? 'Write a custom message (required)' : 'Write a custom message (optional)'"
                        :hint="customMessageHint"
                        counter
                        maxlength="1000"
                        persistent-hint
                        :required="isCustomMessageRequired"
                        :error-messages="form.errors.get('custom_message')"
                        rows="2"
                        class="pt-0 mb-4">
            </v-textarea>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-btn type="submit"
                 color="primary"
                 :disabled="!isFormValid || form.busy"
                 class="mr-4">
            Send
          </v-btn>
          <v-btn @click.prevent="close">
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>
<script>
import {Form} from 'vform';
import {mapGetters} from 'vuex';
import {clone, find, isArray, isNull, isObject, orderBy} from 'lodash';
import validationService from '../../../services/validationService';
import userContactService from '../../../services/userContactService';
import shareTemplateService from '../../../../../ml/services/shareTemplateService';
import sharedLinkService from '../../../../../ml/services/sharedLinkService';
import moment from 'moment-timezone';
import DOMPurify from 'dompurify';

export default {
  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
    presentation: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      contacts: [],
      loading: false,
      search: '',
      form: new Form({
        contact: null,
        custom_message: null, // MLD-4010
        recipients: [],
        presentation_id: [this.presentation.id],
        send_reminders: false,
        share_template_id: '', // MLD-3916
      }),
      template: null,
      templates: [],
    }
  },
  computed: {
    ...mapGetters([
      'canCreateContact',
      'canEmailSelf', // MLD-5505
      'crmLabel',
      'isAdmin',
      'isMember',
      'team',
      'user',
    ]),
    ...mapGetters('content', [ // MLD-4010
      'presentations',
    ]),
    sharingManyPresentations() { // MLD-4010
      return this.form.presentation_id.length > 1;
    },
    canWriteCustomMessage() { // MLD-3998
      return this.template
        && this.template.is_custom_message_allowed
        && this.template.has_custom_message_tag;
    },
    isCustomMessageAllowed() { // MLD-3998
      return this.template
        && this.template.personal_message_allowed;
    },
    isCustomMessageRequired() { // MLD-3998
      return this.isCustomMessageAllowed
        && this.template.personal_message_required;
    },
    customMessageHint() {
      if (this.isCustomMessageRequired) {
        return 'You must write a custom message to put in the email. The message will be the same for everyone who receives it, so don\'t personalize it. Make sure to follow your company\'s rules for what you can and can\'t write. You\'re not allowed to include links or fancy formatting like bold or italics.'
      } else if (this.isCustomMessageAllowed) {
        return 'Write a custom message to put in the email. The message will be the same for everyone who receives it, so don\'t personalize it. Make sure to follow your company\'s rules for what you can and can\'t write. You\'re not allowed to include links or fancy formatting like bold or italics.'
      }
      return null
    },
    shareablePresentations() { // MLD-4010
      return this.presentations.filter(p => {
        if (this.template && p.share_template_id && p.is_template_required && this.template.id !== p.share_template_id) {
          return false;
        }
        return p.can_be_shared;
      })
    },
    canShareLinks() { // MLD-4010
      return this.team.sharing_enabled
        && this.template
        && this.template.can_share_links
        && this.template.has_shared_links_tag;
    },
    requiredQuickShareTemplate() { // MLD-3997
      if (this.presentation.share_template_id && this.presentation.is_template_required) {
        return find(this.templates, t => t.id === this.presentation.share_template_id);
      }
      return null;
    },
    canSelectTemplate() { // MLD-3916
      return this.templates.length > 0
        && (isNull(this.presentation.share_template_id) || this.presentation.is_template_required === false); // MLD-3997
    },
    isAllergan() {
      return this.team
        && this.team.id === 194;
    },
    isFormValid() {
      return !!this.form.recipients.length;
    },
    recipientIDs() {
      return this.form.recipients.map(r => r.id);
    },
    normalizedContacts() {
      return this.contacts.filter(c => !this.recipientIDs.includes(c.id));
    },
    recipientsContainsMyself() { // MLD-5505
      if (this.form.recipients.length === 0) {
        return false;
      }
      return !!find(this.form.recipients, r => r.email === this.user.email);
    },
  },
  watch: {
    search(val) {
      this.searchContacts(val);
    },
  },
  created() {
    this.fetchData();
  },
  methods: {
    searchContacts(query) {
      if (!query) {
        this.loading = false;
        this.contacts = [];
        return;
      }
      this.loading = true;
      const isEmail = validationService.isEmailValid(query);
      const form = {
        email: isEmail ? query : null,
        query: isEmail ? null : query,
        order_by: 'updated_at',
        order_dir: 'desc',
        limit: 10,
        status: 'Active', // MLD-3304
      }
      return userContactService.search(form, this.user)
        .then(data => {
          if (isArray(data.hits)) {
            this.contacts = data.hits;
          } else {
            this.contacts = data;
          }
        })
        .finally(() => {
          this.loading = false;
        })
    },
    addRecipient(contact) {
      const exists = find(this.form.recipients, r => r.id === contact.id || r.email === contact.email);
      if (exists) {
        return false;
      }
      this.form.recipients.push(clone(contact))
      return true;
    },
    async checkContact() {
      if (this.form.contact) {
        if (isObject(this.form.contact)) {
          let contactID = this.form.contact.id;
          try {
            let contact = await userContactService.getContact(contactID);
            if (contact.is_unsubscribed) {
              this.form.errors.set('contact', 'That email address is unsubscribed.');
            } else if (this.canEmailSelf === false && contact.email === this.user.email) { // MLD-5505
              this.form.errors.set('contact', 'You cannot send emails to yourself.');
            } else if (this.addRecipient(contact)) {
              this.form.errors.clear('contact');
            } else {
              this.form.errors.set('contact', 'That email address is already on the list');
            }
          } catch (e) {
            this.$toast.error('There was a problem selecting that contact.');
            this.form.contact = null;
            this.search = '';
          } finally {
            this.form.contact = null;
            this.search = '';
          }
        }
      }
    },
    removeRecipient(recipient) {
      this.form.recipients = this.form.recipients.filter(r => r.id !== recipient.id);
    },
    initForm() {

    },
    fetchData() {
      shareTemplateService.getQuickShareTemplates()
        .then(data => {
          this.templates = orderBy(data, ['slug'], ['asc']);
          if (this.presentation.share_template_id) { // MLD-3916
            this.form.share_template_id = this.presentation.share_template_id;
          }
        })
        .catch(err => {
          this.templates = [];
        })

      const templateID = this.presentation.share_template_id;
      if (templateID) {
        shareTemplateService.getTemplate(templateID)
          .then(data => {
            this.template = data;
          })
          .catch(err => {
            this.template = null;
          })
      }
    },
    selectTemplate() { // MLD-4010
      const templateID = this.form.share_template_id;
      if (templateID) {
        shareTemplateService.getTemplate(templateID)
          .then(data => {
            this.template = data;
          })
          .catch(err => {
            this.template = null;
          })
      }
    },
    close() {
      this.loading = false;
      this.search = '';
      this.form.reset();
      this.$emit('close');
    },
    submit() {
      if (this.form.busy) {
        return;
      }
      if (!this.isFormValid) {
        return;
      }
      this.form.busy = true; // MLD-6787

      if (this.sharingManyPresentations) { // MLD-4010
        sharedLinkService.quickShare(this.form)
          .then(() => {
            this.$toast.success(`${DOMPurify.sanitize(this.presentation.name)} shared successfully`);
            this.close();
          })
          .catch(err => {
            //this.form.errors.set(this.form.extractErrors(err.response));
            this.$toast.error(DOMPurify.sanitize(err.message));
          })
          .finally(() => { // MLD-6787
            this.form.busy = false;
          })
      } else {
        sharedLinkService.sharePresentation(this.presentation.id, this.form.data())
          .then(() => {
            this.$toast.success(`${DOMPurify.sanitize(this.presentation.name)} shared successfully`);
            this.close();
          })
          .catch(err => {
            this.form.errors.set(this.form.extractErrors(err.response));
            this.$toast.error(DOMPurify.sanitize(err.message));
          })
          .finally(() => { // MLD-6787
            this.form.busy = false;
          })
      }
    },
    syncFromMoxie() { // MLD-3237
      this.showSyncDialog = true;
      const startAt = moment().startOf('day');
      userContactService.syncFromCRM(this.user.id, startAt.format())
        .then(data => {
          if (data.enriching) {

          } else {
            this.$toast.error('Your contacts could not be synced from Moxie.');
            this.showSyncDialog = false;
          }
        })
    },
  }
}
</script>
